import * as React from 'react';
import { HealthMetricDeserialisedDataV2 } from '@leagueplatform/dashboard-api';
import { LineGraph } from '#components/charts/core/line-graph/index.ts';
import {
  CHART_VIEW,
  CHART_VIEWS,
} from '#components/charts/constants/chart-views.ts';
import { RangeGraph } from '#components/charts/core/range-graph/index.ts';
import { useHealthMetricValues } from '#components/charts/core/range-graph/utils/use-health-metric-values.ts';
import { shapes } from '#components/charts/core/xychart/primitives/Shape.tsx';
import { useIntl } from '@leagueplatform/locales';
import {
  DATE_FORMAT,
  useFormatDateByUserProfileLocation,
} from '@leagueplatform/web-common';
import { BarGraph } from '#components/charts/core/bar-graph/index.ts';
import { MultipleMeasurementsTooltip } from '#components/charts/core/chart-tooltip/multiple-measurements-tooltip.tsx';
import { getHealthMetricUnitDisplayValue } from '#utils/get-health-metric-unit-display-value.ts';
import { SingleMeasurementTooltip } from '#components/charts/core/chart-tooltip/v2/single-measurement-tooltip.tsx';

// Example:
// Range: May 31, 2023, Systolic 90-100 millimeters of mercury
// Single: May 31, 2023, Systolic 90 systolic
// Multiple data types: July 31, 2019, Systolic 60 mmHg, Diastolic 10-40 mmHg
type Datum = ReturnType<typeof useHealthMetricValues>[number];

const generateLabel = (singleDatum: Datum) => {
  if (singleDatum.value === null) {
    return '';
  }

  const metricName = singleDatum.metric.data.name;
  const value = Array.isArray(singleDatum.value)
    ? singleDatum.value.join('–')
    : singleDatum.value;
  const unitName = getHealthMetricUnitDisplayValue(
    singleDatum.value,
    singleDatum.metric.data.unit,
  );

  return `${metricName} ${value} ${unitName}`;
};

function useDatumLabel() {
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();

  return (datum: Datum | Datum[]) => {
    if (Array.isArray(datum)) {
      const label = datum.filter((d) => d.value !== null).map(generateLabel);
      const date = formatDateWithUserProfileLocation(
        new Date(datum[0].timestamp),
        DATE_FORMAT,
      );

      if (label.length === 0) {
        return '';
      }

      return `${date}, ${label.join(', ')}`;
    }

    if (datum.value === null) {
      return '';
    }

    const label = generateLabel(datum);
    const date = formatDateWithUserProfileLocation(
      new Date(datum.timestamp),
      DATE_FORMAT,
    );

    return `${date}, ${label}`;
  };
}

type ChartProps = {
  chartView: CHART_VIEW;
  chartData: HealthMetricDeserialisedDataV2['data'][number];
};

export function Chart({ chartView, chartData }: ChartProps) {
  const { $tifelse } = useIntl();
  const { screenReaderTitle, screenReaderDescription } = chartData;
  const axisLeftLabel = chartData.axisLeftLabel.toLocaleUpperCase();
  const axisBottomLabel = $tifelse(
    chartView === CHART_VIEWS.WEEKLY,
    'DAY_OF_THE_WEEK',
    'DAY_OF_THE_MONTH',
  );

  const generateDatumLabel = useDatumLabel();

  const data = useHealthMetricValues(chartData.values.data);

  switch (chartData.chartType) {
    case 'bar': {
      return (
        <BarGraph
          chartView={chartView}
          data={data}
          accessors={{
            xAccessor: (datum) => datum.timestamp,
            yAccessor: (datum) => datum.value as number,
          }}
          title={screenReaderTitle}
          description={screenReaderDescription}
          axisLeftLabel={axisLeftLabel}
          axisBottomLabel={axisBottomLabel}
          ariaDatumLabel={generateDatumLabel}
          renderTooltip={(datum) => <SingleMeasurementTooltip datum={datum} />}
          yMaxFromEnums={0}
        />
      );
    }
    case 'line': {
      return (
        <LineGraph
          chartView={chartView}
          data={data}
          accessors={{
            xAccessor: (datum) => datum.timestamp,
            yAccessor: (datum) => datum.value as number,
          }}
          title={screenReaderTitle}
          description={screenReaderDescription}
          axisLeftLabel={axisLeftLabel}
          axisBottomLabel={axisBottomLabel}
          ariaDatumLabel={generateDatumLabel}
          renderTooltip={(datum) => <SingleMeasurementTooltip datum={datum} />}
          yMaxFromEnums={0}
        />
      );
    }
    case 'range': {
      return (
        <RangeGraph
          chartView={chartView}
          data={data}
          accessors={{
            xAccessor: (datum) => datum.timestamp,
            yAccessor: (datum) => datum.value,
          }}
          title={screenReaderTitle}
          description={screenReaderDescription}
          axisLeftLabel={axisLeftLabel}
          axisBottomLabel={axisBottomLabel}
          ariaDatumLabel={generateDatumLabel}
          getShape={(datum) => shapes[datum.metric.data.order]}
          renderTooltip={(datum) => (
            <MultipleMeasurementsTooltip data={datum} />
          )}
          yMaxFromEnums={0}
        />
      );
    }
    default:
      return null;
  }
}
